import React, { useMemo } from 'react'
import { Icon } from 'antd'
import './ImageGenerationSummary.scss'

const formatCountLabel = (count, singular, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`

function ImageGenerationSummary({ stats = {} }) {
  const {
    total = 0,
    completed = 0,
    failed = 0,
    generating = 0,
    pending = 0,
    state: stateFromStats
  } = stats

  const summary = useMemo(() => {
    const readyText = formatCountLabel(completed, 'ready item')
    const generatingText = `${Math.max(generating, 1)} of ${total}`
    const issueParts = [
      failed > 0 ? formatCountLabel(failed, 'error') : null,
      pending > 0 ? formatCountLabel(pending, 'pending item') : null
    ].filter(Boolean)

    if (total === 0) {
      return {
        tone: 'empty',
        icon: 'plus-circle',
        label: 'No Images Yet',
        statusText: 'Add shots to begin'
      }
    }

    if (stateFromStats === 'generating' || generating > 0) {
      return {
        tone: 'loading',
        icon: 'loading',
        label: `Generating ${generatingText}`,
        statusText: issueParts.length > 0 ? issueParts.join(', ') : readyText
      }
    }

    if (failed > 0 || stateFromStats === 'attention') {
      return {
        tone: 'attention',
        icon: 'warning',
        label: readyText,
        statusText: issueParts.length > 0 ? issueParts.join(', ') : 'Needs attention'
      }
    }

    if (completed === total || stateFromStats === 'complete') {
      return {
        tone: 'success',
        icon: 'check-circle',
        label: readyText,
        statusText: 'All images ready'
      }
    }

    return {
      tone: 'neutral',
      icon: 'clock-circle',
      label: readyText,
      statusText: pending > 0 ? formatCountLabel(pending, 'pending item') : 'Waiting to start'
    }
  }, [completed, failed, generating, pending, stateFromStats, total])

  return (
    <div
      className={`image-generation-summary image-generation-summary--${summary.tone}`}
    >
      <div className="image-generation-summary__main">
        <div className="image-generation-summary__inline">
          <div
            className={`image-generation-summary__badge image-generation-summary__badge--${summary.tone}`}
          >
            <Icon type={summary.icon} />
            <span>{summary.label}</span>
          </div>

          <div className="image-generation-summary__meta">
            {summary.statusText}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageGenerationSummary
